import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { teamsApi, type Team, type TeamMemberInfo } from '../teams/teamsApi';
import { DeleteButton } from '../components/DeleteButton';

export default function TeamsPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const { getToken, refresh } = useAuth();

  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [members, setMembers] = useState<TeamMemberInfo[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState('');

  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [addingMember, setAddingMember] = useState(false);

  const loadTeams = useCallback(() => {
    setLoading(true);
    teamsApi
      .list(workspaceId, getToken, refresh)
      .then(data => setTeams(data.teams))
      .catch(() => setError('Failed to load teams'))
      .finally(() => setLoading(false));
  }, [workspaceId, getToken, refresh]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setError('');
    try {
      await teamsApi.create(workspaceId, newName.trim(), newDescription.trim(), getToken, refresh);
      setNewName('');
      setNewDescription('');
      setShowCreateForm(false);
      loadTeams();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create team');
    } finally {
      setCreating(false);
    }
  }

  async function handleExpand(teamId: string) {
    if (expandedId === teamId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(teamId);
    setMembers([]);
    setMembersError('');
    setMembersLoading(true);
    try {
      const data = await teamsApi.get(workspaceId, teamId, getToken, refresh);
      setMembers(data.members);
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : 'Failed to load team');
    } finally {
      setMembersLoading(false);
    }
  }

  async function handleAddMember(e: FormEvent, teamId: string) {
    e.preventDefault();
    if (!newMemberEmail.trim()) return;
    setAddingMember(true);
    setMembersError('');
    try {
      const member = await teamsApi.addMember(workspaceId, teamId, newMemberEmail.trim(), getToken, refresh);
      setMembers(prev => [...prev, member]);
      setNewMemberEmail('');
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  }

  async function handleRemoveMember(teamId: string, userId: string) {
    setMembersError('');
    try {
      await teamsApi.removeMember(workspaceId, teamId, userId, getToken, refresh);
      setMembers(prev => prev.filter(m => m.userId !== userId));
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : 'Failed to remove member');
    }
  }

  async function handleDelete(teamId: string) {
    setError('');
    try {
      await teamsApi.remove(workspaceId, teamId, getToken, refresh);
      setTeams(prev => prev.filter(t => t.teamId !== teamId));
      if (expandedId === teamId) setExpandedId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete team');
    }
  }

  return (
    <main className="page">
      <div className="toolbar" style={{ justifyContent: 'space-between' }}>
        <h1>Teams</h1>
        {!showCreateForm && (
          <button type="button" onClick={() => setShowCreateForm(true)}>
            + Create team
          </button>
        )}
      </div>

      {error && <p className="error">{error}</p>}

      {showCreateForm && (
        <form onSubmit={handleCreate} className="form-card">
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Team name" required autoFocus />
          <input
            value={newDescription}
            onChange={e => setNewDescription(e.target.value)}
            placeholder="Description (optional)"
          />
          <div className="form-actions">
            <button type="submit" disabled={creating}>
              {creating ? 'Creating…' : 'Create team'}
            </button>
            <button type="button" onClick={() => setShowCreateForm(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading && <p className="muted">Loading…</p>}
      {!loading && teams.length === 0 && <p className="muted">No teams yet.</p>}

      <ul className="card-list">
        {teams.map(team => (
          <li key={team.teamId} className="card">
            <strong>{team.name}</strong>
            {team.description && <p className="muted" style={{ margin: '4px 0' }}>{team.description}</p>}
            <div className="toolbar--split" style={{ marginTop: 8 }}>
              <button onClick={() => handleExpand(team.teamId)}>
                {expandedId === team.teamId ? 'Hide members' : 'Show members'}
              </button>
              <DeleteButton onConfirm={() => handleDelete(team.teamId)} title="Delete team" />
            </div>

            {expandedId === team.teamId && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
                {membersLoading && <p className="muted">Loading members…</p>}
                {membersError && <p className="error">{membersError}</p>}

                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {members.map(member => (
                    <li
                      key={member.userId}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}
                    >
                      <span>
                        {member.displayName} ({member.email})
                      </span>
                      <DeleteButton onConfirm={() => handleRemoveMember(team.teamId, member.userId)} title="Remove member" />
                    </li>
                  ))}
                </ul>

                <form
                  onSubmit={e => handleAddMember(e, team.teamId)}
                  style={{ display: 'flex', gap: 8, marginTop: 8 }}
                >
                  <input
                    type="email"
                    value={newMemberEmail}
                    onChange={e => setNewMemberEmail(e.target.value)}
                    placeholder="Member email"
                    required
                  />
                  <button type="submit" disabled={addingMember}>
                    {addingMember ? 'Adding…' : 'Add member'}
                  </button>
                </form>
              </div>
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}
